import React from 'react';
import { Tooltip, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import useHirePortTheme from '../../../hooks/useHirePortTheme';
import {
    StageCard as StyledStageCard,
    StageCardHeader,
    StageNameWrapper,
    StageName,
    StageTypeLabel,
    StageTypeIcon,
    StageLabelText,
    StageOrder,
    DragHandle,
    SelectionCorner,
    StageCardCheckbox
} from '../styles';
import { DefaultStageMappingType } from '../../../api/integrations';
import { STAGE_TYPES } from '../types/stageTypes';
import { getStageTypeColor } from '../utils/stageColors';

export interface StageCardProps {
    id: string;
    name: string;
    stageType?: DefaultStageMappingType | null;
    order?: number;
    isUnmapped?: boolean;
    isSelected: boolean;
    isChanged?: boolean;
    isInterview?: boolean;
    dragHandleProps: any;
    innerRef: any;
    draggableProps: any;
    onToggleSelection: (id: string) => void;
    onToggleAlwaysUnmap?: (id: string, unmapped: boolean) => void;
}

const StageCard: React.FC<StageCardProps> = ({
    id,
    name,
    stageType,
    order,
    isUnmapped,
    isSelected,
    isChanged,
    isInterview,
    dragHandleProps,
    innerRef,
    draggableProps,
    onToggleSelection,
    onToggleAlwaysUnmap
}) => {
    const { t } = useTranslation();
    const theme = useHirePortTheme();

    // Get stage type label for display
    const stageTypeLabel = isUnmapped
        ? t('ats.ALWAYS_UNMAP')
        : stageType
            ? STAGE_TYPES.find(t => t.value === stageType)?.label || stageType
            : t('ats.NOT_MAPPED');

    // Add a helper tooltip text that explains the difference
    const unmappedTooltipText = t('ats.ALWAYS_UNMAP_TOOLTIP');

    // Determine the color based on state
    const labelColor = isUnmapped ? '#ff4d4f' : (stageType ? getStageTypeColor(stageType) : '#888');
    const iconColor = isUnmapped ? '#ff4d4f' : (stageType ? getStageTypeColor(stageType) : undefined);

    return (
        <StyledStageCard
            ref={innerRef}
            {...draggableProps}
            {...dragHandleProps}
            $stageType={iconColor}
            $isSelected={isSelected}
            onClick={() => onToggleSelection(id)}
        >
            <StageCardHeader>
                <StageNameWrapper>
                    <StageCardCheckbox
                        checked={isSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => onToggleSelection(id)}
                    />
                    <StageName title={name}>
                        {name}
                    </StageName>
                </StageNameWrapper>
                <DragHandle
                    onClick={(e) => e.stopPropagation()}
                >
                    <span style={{ fontSize: '15px', lineHeight: 1 }}>≡</span>
                </DragHandle>
            </StageCardHeader>

            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '4px'
            }}>
                <StageTypeLabel>
                    <StageTypeIcon $stageType={iconColor} />
                    <StageLabelText style={{ color: labelColor }}>
                        {stageTypeLabel}
                    </StageLabelText>
                    {isInterview && order !== undefined && (
                        <StageOrder>
                            #{order + 1}
                        </StageOrder>
                    )}
                </StageTypeLabel>

                {onToggleAlwaysUnmap && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Tooltip
                            title={unmappedTooltipText}
                            mouseEnterDelay={0.5}
                            mouseLeaveDelay={0.1}
                        >
                            <Switch
                                size="small"
                                checked={isUnmapped}
                                onChange={(checked) => {
                                    if (onToggleAlwaysUnmap) {
                                        onToggleAlwaysUnmap(id, checked);
                                    }
                                }}
                                style={{
                                    marginRight: '8px',
                                    backgroundColor: isUnmapped ? '#ff4d4f' : undefined
                                }}
                            />
                        </Tooltip>
                        <span
                            style={{
                                fontSize: '12px',
                                color: isUnmapped ? '#ff4d4f' : '#8c8c8c'
                            }}
                        >
                            {t('ats.ALWAYS_UNMAP')}
                        </span>
                    </div>
                )}
            </div>

            {/* Changed indicator */}
            {isChanged && (
                <Tooltip title={t('ats.STAGE_HAS_CHANGES')}>
                    <div
                        style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: theme.primaryColor,
                        }}
                    />
                </Tooltip>
            )}

            {/* Selection indicator */}
            {isSelected && <SelectionCorner />}
        </StyledStageCard>
    );
};

export default StageCard; 